/**
 * "Perfect Your Upload" guidance sheet (P2-T09).
 *
 * Reusable bottom sheet shown the first time a user picks a photo for a given
 * tool: 3 good and 3 bad examples, then "I got it". It appears once per tool
 * (remembered in localStorage) and never blocks: if storage is unavailable the
 * sheet simply shows each time, and a dismissed sheet never stops a later tap.
 *
 *   withUploadGuide('retouch', () => fileInput.click());
 *
 * `proceed` runs synchronously inside the "I got it" click handler, so browsers
 * still treat the file picker it opens as user-initiated. The examples are
 * original illustrations (no real people, no stock photos).
 */
import { icon } from './icon.js';

export type UploadGuideKind = 'face' | 'photo';

/** Tools whose input is mainly a person's face/body; everything else is a generic photo. */
const FACE_TOOLS: ReadonlySet<string> = new Set([
  'change_outfit',
  'hair_salon',
  'makeup_styles',
  'face_expression',
  'face_swap_image',
  'face_swap_video',
  'retouch',
  'reshape_body',
]);

const STORAGE_KEY = 'loomia.uploadGuide.seen.v1';

export function uploadGuideKind(tool: string): UploadGuideKind {
  return FACE_TOOLS.has(tool) ? 'face' : 'photo';
}

function readSeen(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function hasSeenUploadGuide(tool: string): boolean {
  return readSeen().includes(tool);
}

function markSeen(tool: string): void {
  try {
    const seen = readSeen();
    if (!seen.includes(tool)) localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen, tool]));
  } catch {
    /* storage blocked: the sheet just shows again next time */
  }
}

// ---------------------------------------------------------------------------
// Copy + illustrations
// ---------------------------------------------------------------------------

type Variant = 'clear' | 'bright' | 'framed' | 'blur' | 'angle' | 'covered' | 'dark' | 'filtered';

interface Example {
  variant: Variant;
  label: string;
}

const COPY: Record<UploadGuideKind, { sub: string; good: Example[]; bad: Example[] }> = {
  face: {
    sub: 'Use a clear, well-lit photo with a natural face for best AI results.',
    good: [
      { variant: 'clear', label: 'Clear & front-facing' },
      { variant: 'bright', label: 'Good lighting' },
      { variant: 'framed', label: 'Whole face in frame' },
    ],
    bad: [
      { variant: 'blur', label: 'Blurry' },
      { variant: 'angle', label: 'Side angle' },
      { variant: 'covered', label: 'Face covered' },
    ],
  },
  photo: {
    sub: 'Use a sharp, well-lit photo with the subject fully in view for best AI results.',
    good: [
      { variant: 'clear', label: 'Sharp & in focus' },
      { variant: 'bright', label: 'Good lighting' },
      { variant: 'framed', label: 'Subject fully in frame' },
    ],
    bad: [
      { variant: 'blur', label: 'Blurry' },
      { variant: 'dark', label: 'Too dark' },
      { variant: 'filtered', label: 'Heavy filters' },
    ],
  },
};

/** One small illustrated example tile. Pure SVG, so nothing to download or license. */
function figure(kind: UploadGuideKind, variant: Variant): string {
  const bg =
    variant === 'bright'
      ? ['#c4b5fd', '#f5d0fe']
      : variant === 'dark'
        ? ['#312e81', '#4c1d95']
        : ['#7c3aed', '#c026d3'];
  const id = `ug-${kind}-${variant}`;
  let body: string;
  if (kind === 'face') {
    const cx = variant === 'angle' ? 76 : 60;
    const head =
      variant === 'angle'
        ? `<ellipse cx="${cx}" cy="52" rx="15" ry="22" fill="#fde68a" transform="rotate(14 ${cx} 52)"/>`
        : `<circle cx="${cx}" cy="${variant === 'framed' ? 54 : 50}" r="${variant === 'framed' ? 20 : 24}" fill="#fde68a"/>`;
    const eyes =
      variant === 'angle'
        ? '<circle cx="84" cy="50" r="2" fill="#4c1d95"/>'
        : '<circle cx="51" cy="50" r="2.2" fill="#4c1d95"/><circle cx="69" cy="50" r="2.2" fill="#4c1d95"/><path d="M52 61q8 6 16 0" stroke="#4c1d95" stroke-width="2" fill="none" stroke-linecap="round"/>';
    const cover =
      variant === 'covered'
        ? '<rect x="34" y="52" width="52" height="24" rx="9" fill="#1f2937"/><rect x="40" y="40" width="40" height="9" rx="4" fill="#1f2937"/>'
        : '';
    body = `<path d="M18 120c0-26 18-40 42-40s42 14 42 40z" fill="#fbcfe8"/>${head}${variant === 'covered' ? '' : eyes}${cover}`;
  } else {
    const shape =
      variant === 'framed'
        ? '<circle cx="60" cy="60" r="17" fill="#fde68a"/><path d="M34 98l26-34 26 34z" fill="#fbcfe8"/>'
        : '<circle cx="60" cy="52" r="22" fill="#fde68a"/><path d="M26 104l34-46 34 46z" fill="#fbcfe8"/><rect x="76" y="70" width="22" height="26" rx="5" fill="#a7f3d0"/>';
    body = shape;
  }
  const filter =
    variant === 'blur'
      ? 'filter="url(#' + id + '-b)"'
      : variant === 'filtered'
        ? 'filter="url(#' + id + '-f)"'
        : '';
  const dim = variant === 'dark' ? '<rect width="120" height="120" fill="#000" opacity=".55"/>' : '';
  return `
    <svg viewBox="0 0 120 120" role="img" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="${id}-g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${bg[0]}"/><stop offset="1" stop-color="${bg[1]}"/></linearGradient>
        <filter id="${id}-b"><feGaussianBlur stdDeviation="4.5"/></filter>
        <filter id="${id}-f"><feColorMatrix type="matrix" values="1.6 0 0 0 -.15  0 .3 0 0 .05  0 0 1.9 0 -.1  0 0 0 1 0"/></filter>
      </defs>
      <g ${filter}>
        <rect width="120" height="120" fill="url(#${id}-g)"/>
        ${body}
        ${dim}
      </g>
    </svg>`;
}

function tile(kind: UploadGuideKind, ex: Example, good: boolean): string {
  return `
    <li class="ug-ex ${good ? 'is-good' : 'is-bad'}">
      <span class="ug-fig">
        ${figure(kind, ex.variant)}
        <span class="ug-badge" aria-hidden="true">${icon(good ? 'check' : 'close')}</span>
      </span>
      <span class="ug-cap"><span class="sr-only">${good ? 'Good: ' : 'Avoid: '}</span>${ex.label}</span>
    </li>`;
}

export function uploadGuideHtml(kind: UploadGuideKind): string {
  const c = COPY[kind];
  return `
    <div class="sheet ug-sheet" role="dialog" aria-modal="true" aria-labelledby="ug-title">
      <div class="sheet-handle"></div>
      <h2 class="page-title ug-title" id="ug-title">Perfect Your Upload</h2>
      <p class="muted">${c.sub}</p>
      <h3 class="ug-h is-good">${icon('check_circle', { fill: true })}Good examples</h3>
      <ul class="ug-row">${c.good.map((e) => tile(kind, e, true)).join('')}</ul>
      <h3 class="ug-h is-bad">${icon('close')}Bad examples</h3>
      <ul class="ug-row">${c.bad.map((e) => tile(kind, e, false)).join('')}</ul>
      <button class="btn btn-primary btn-block" data-ug-ok>I got it</button>
    </div>`;
}

// ---------------------------------------------------------------------------
// Behaviour
// ---------------------------------------------------------------------------

/**
 * Run `proceed` (usually "open the file picker"). The first time for each tool
 * the guidance sheet is shown first and `proceed` runs when the user taps
 * "I got it". Dismissing with the backdrop or Escape marks it seen but does not
 * proceed, so the next tap goes straight to the picker.
 */
export function withUploadGuide(tool: string, proceed: () => void): void {
  if (document.getElementById('upload-guide')) return; // already open
  if (hasSeenUploadGuide(tool)) {
    proceed();
    return;
  }
  const el = document.createElement('div');
  el.id = 'upload-guide';
  el.className = 'sheet-backdrop';
  el.innerHTML = uploadGuideHtml(uploadGuideKind(tool));
  document.body.appendChild(el);
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const close = (): void => {
    markSeen(tool);
    el.classList.remove('open');
    document.removeEventListener('keydown', onKey);
    window.setTimeout(() => el.remove(), 200);
    previouslyFocused?.focus?.();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  el.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-ug-ok]')) {
      close();
      proceed(); // still inside the click handler → counts as a user gesture
    } else if (t === el) {
      close();
    }
  });
  document.addEventListener('keydown', onKey);
  requestAnimationFrame(() => {
    el.classList.add('open');
    el.querySelector<HTMLElement>('[data-ug-ok]')?.focus();
  });
}
